import {
  CrashType,
  IScore,
  RiskFactor,
  riskFactorColors,
  RiskFactors,
  Score,
  ScoreEntry,
  typedEntries,
} from "@keita/lanepatrol-sdk";
import BBIcon from "@keita/lanepatrol-sdk/assets/score/BB.jpg";
import BPIcon from "@keita/lanepatrol-sdk/assets/score/BP.jpg";
import SBIcon from "@keita/lanepatrol-sdk/assets/score/SB.jpg";
import VBIcon from "@keita/lanepatrol-sdk/assets/score/VB.jpg";
import { t } from "i18next";
import { GeoJsonNodeProps } from "../../cra/models/CustomGeoJsonProperties";

export const tScoreType = (key: keyof Score) => {
  if (key === "global") return t("coding:global");
  return t(scoreI18nLabels[key as CrashType]);
};

const scoreI18nLabels = {
  [CrashType.vehicleToBicycle]: "coding:vehicleToBicycle",
  [CrashType.bicycleToBicycle]: "coding:bicycleToBicycle",
  [CrashType.bicycleToPedestrian]: "coding:bicycleToPedestrian",
  [CrashType.singleBicycle]: "coding:singleBicycle",
};

export const scoreCrashTypesIcons = {
  [CrashType.vehicleToBicycle]: VBIcon,
  [CrashType.bicycleToBicycle]: BBIcon,
  [CrashType.bicycleToPedestrian]: BPIcon,
  [CrashType.singleBicycle]: SBIcon,
};

export const selectedCrashTypeColors = {
  [CrashType.vehicleToBicycle]: "#27BDFF",
  [CrashType.bicycleToBicycle]: "#6ad1ff",
  [CrashType.bicycleToPedestrian]: "#b4e8ff",
  [CrashType.singleBicycle]: "#c7ecff",
};

export const calculateRiskFactors = (score: IScore<number>): RiskFactors => {
  const riskFactors = {
    vehicleToBicycle: calculateRiskFactor(
      score.vehicleToBicycle,
      CrashType.vehicleToBicycle
    ),
    bicycleToBicycle: calculateRiskFactor(
      score.bicycleToBicycle,
      CrashType.bicycleToBicycle
    ),
    bicycleToPedestrian: calculateRiskFactor(
      score.bicycleToPedestrian,
      CrashType.bicycleToPedestrian
    ),
    singleBicycle: calculateRiskFactor(
      score.singleBicycle,
      CrashType.singleBicycle
    ),
  };
  return {
    ...riskFactors,
    global: calculateGlobalRiskFactor(Object.values(riskFactors)),
  };
};

export const calculateRiskFactor = (
  score: number,
  crashType?: CrashType
): RiskFactor => {
  switch (crashType) {
    case CrashType.vehicleToBicycle:
      if (score < 10) return RiskFactor.Low;
      else if (score < 25) return RiskFactor.Medium;
      else if (score < 60) return RiskFactor.High;
      else return RiskFactor.Extreme;
    default:
      if (score < 5) return RiskFactor.Low;
      else if (score < 10) return RiskFactor.Medium;
      else if (score < 20) return RiskFactor.High;
      else return RiskFactor.Extreme;
  }
};

export const tRiskFactor = (riskFactor: RiskFactor): string =>
  t(`coding:${riskFactor.replace("-", ".")}`);

export const calculateGlobalRiskFactor = (factors: Array<RiskFactor>) => {
  return factors.includes(RiskFactor.Extreme)
    ? RiskFactor.Extreme
    : factors.includes(RiskFactor.High)
    ? RiskFactor.High
    : factors.includes(RiskFactor.Medium)
    ? RiskFactor.Medium
    : RiskFactor.Low;
};

export const parseGeoJsonNodeScore = (props: GeoJsonNodeProps) => {
  if (!props["score.global"]) return null;
  return scoreFromValues({
    global: parseFloat(props["score.global"]),
    bicycleToBicycle: parseFloat(props["score.bicycleToBicycle"]!),
    bicycleToPedestrian: parseFloat(props["score.bicycleToPedestrian"]!),
    singleBicycle: parseFloat(props["score.singleBicycle"]!),
    vehicleToBicycle: parseFloat(props["score.vehicleToBicycle"]!),
  });
};

export const scoreFromValues = (scoreValues: IScore<number>): Score => {
  const riskFactors = calculateRiskFactors(scoreValues);

  const entry = (ct: keyof Score): ScoreEntry => ({
    value: scoreValues[ct],
    riskFactor: riskFactors[ct],
    color: riskFactorColors[riskFactors[ct]],
  });

  return {
    global: entry("global"),
    bicycleToBicycle: entry("bicycleToBicycle"),
    bicycleToPedestrian: entry("bicycleToPedestrian"),
    singleBicycle: entry("singleBicycle"),
    vehicleToBicycle: entry("vehicleToBicycle"),
  };
};

export const scoreToGeoJsonNodeScore = (score: Score) => {
  const props: Record<string, string> = {};
  for (let [scoreType, entry] of typedEntries<keyof Score, ScoreEntry>(score)) {
    props[`score.${scoreType}`] = entry.value.toString();
  }
  return props;
};
